// src/services/api.js
import { getApiBaseUrl, getAppIdentifier } from '../config';
import i18n from 'i18next';

// ✅ Dynamic API base URL
const API_BASE = getApiBaseUrl();

class ApiService {
  constructor() {
    // 429 backoff state: when we get rate-limited, pause ALL requests briefly
    this._backoffUntil = 0;
  }

  // ✅ Updated helper method with credentials, app identifier, and 429 backoff
  async request(endpoint, options = {}) {
    // If we're in a backoff period, reject immediately to avoid flooding
    const now = Date.now();
    if (now < this._backoffUntil) {
      const error = new Error('Rate limited – backing off');
      error.status = 429;
      error.isBackoff = true;
      throw error;
    }

    try {
      const appId = getAppIdentifier();

      const response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-App-Identifier': appId || '',
          'Accept-Language': i18n.language || 'en',
          ...options.headers,
        },
      });

      // Handle 429 Too Many Requests – enable backoff for ALL subsequent requests
      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After');
        const backoffMs = retryAfter ? parseInt(retryAfter, 10) * 1000 : 60000; // default 60s
        this._backoffUntil = Date.now() + backoffMs;
        console.warn(`⚠️ 429 Rate Limited on ${endpoint} – backing off for ${backoffMs / 1000}s`);
        const error = new Error('Too many requests, please try again later.');
        error.status = 429;
        throw error;
      }

      if (!response.ok) {
        try {
          const errorData = await response.json();
          const error = new Error(errorData.message || `Request failed with status ${response.status}`);
          error.status = response.status;
          if (errorData.validationErrors) error.validationErrors = errorData.validationErrors;
          if (errorData.errors) error.validationErrors = errorData.errors;
          if (errorData.validationWarnings) error.validationWarnings = errorData.validationWarnings;
          if (errorData.warnings) error.validationWarnings = errorData.warnings;
          if (errorData.details) error.details = errorData.details;
          if (errorData.code) error.code = errorData.code;
          throw error;
        } catch (jsonError) {
          if (jsonError instanceof Error && (jsonError.status || jsonError.validationErrors || jsonError.details)) {
            throw jsonError;
          }
          const error = new Error(`Request failed with status ${response.status}: ${response.statusText}`);
          error.status = response.status;
          throw error;
        }
      }

      return await response.json();
    } catch (error) {
      if (error.status && error.status >= 500) {
        console.error('API Server Error:', error);
      }
      throw error;
    }
  }

  // Kindergarten API
  async getKindergartens() {
    return this.request('/kindergartens');
  }

  async createKindergarten(data) {
    return this.request('/kindergartens/create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateKindergarten(id, data) {
    return this.request(`/kindergartens/update/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteKindergarten(id) {
    return this.request(`/kindergartens/delete/${id}`, {
      method: 'DELETE',
    });
  }

  // Groups API
  async getGroups(kindergartenId = null) {
    const query = kindergartenId ? `?kindergartenId=${kindergartenId}` : '';
    return this.request(`/groups${query}`);
  }

  async getGroup(id) {
    return this.request(`/groups/${id}`);
  }

  async createGroup(data) {
    return this.request('/groups/create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateGroup(id, data) {
    return this.request(`/groups/update/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteGroup(id) {
    return this.request(`/groups/delete/${id}`, {
      method: 'DELETE',
    });
  }

  async assignTeachersToGroup(groupId, teacherIds) {
    return this.request(`/groups/${groupId}/assign-teachers`, {
      method: 'POST',
      body: JSON.stringify({ teacherIds }),
    });
  }

  async getGroupChildren(groupId) {
    return this.request(`/groups/${groupId}/children`);
  }

  async getGroupParents(groupId) {
    return this.request(`/groups/${groupId}/parents`);
  }

  // Users (Teachers/Parents) API
  async getUsers() {
    return this.request('/users');
  }

  async getTeachers(kindergartenId = null) {
    const query = kindergartenId ? `?kindergartenId=${kindergartenId}` : '';
    return this.request(`/users/teachers${query}`);
  }

  async getParents(kindergartenId) {
    const query = kindergartenId ? `?kindergartenId=${kindergartenId}` : '';
    return this.request(`/users/parents${query}`);
  }

  async createUser(data) {
    return this.request('/users/create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUser(id, data) {
    return this.request(`/users/update/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteUser(id) {
    return this.request(`/users/delete/${id}`, {
      method: 'DELETE',
    });
  }

  async checkAccessCode(accessCode) {
    return this.request(`/users/check-access-code/${accessCode}`);
  }

  async sendInvitationEmail(userId) {
    return this.request(`/users/${userId}/send-invitation`, {
      method: 'POST',
    });
  }

  // Children API
  async getChildren() {
    return this.request('/children');
  }

  async getChildrenByParent(parentId) {
    return this.request(`/children/parent/${parentId}`);
  }

  async getChildrenByGroup(groupId) {
    return this.request(`/children/group/${groupId}`);
  }

  async createChild(data) {
    return this.request('/children/create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateChild(id, data) {
    return this.request(`/children/update/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteChild(id) {
    return this.request(`/children/delete/${id}`, {
      method: 'DELETE',
    });
  }

  // Child Billing Recipients API
  async addBillingRecipient(childId, data) {
    return this.request(`/children/${childId}/billing-recipients`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateBillingRecipient(childId, recipientIndex, data) {
    return this.request(`/children/${childId}/billing-recipients/${recipientIndex}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteBillingRecipient(childId, recipientIndex) {
    return this.request(`/children/${childId}/billing-recipients/${recipientIndex}`, {
      method: 'DELETE',
    });
  }

  // Teacher-specific APIs
  async getTeacherGroups(teacherId) {
    return this.request(`/groups/teacher/${teacherId}`);
  }

  async getGroupChildrenDetailed(groupId) {
    return this.request(`/groups/${groupId}/children`);
  }

  async getGroupParentsDetailed(groupId) {
    return this.request(`/groups/${groupId}/parents`);
  }

  // Messages API
  async sendInfoMessage(data) {
    return this.request('/messages/send-info', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async sendApprovalMessage(data) {
    return this.request('/messages/send-approval', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Paged lists take a (date, id) cursor: the id breaks ties between rows with
  // the same date, which the server needs to return each row exactly once.
  // withQuiz: only the rows holding a quiz (the "📊 With quiz" filter; the
  // total stays the whole count).
  async getUserMessages(userId, type = null, { limit, before, beforeId, withQuiz } = {}) {
    const params = new URLSearchParams();
    if (type) params.set('type', type);
    if (limit) params.set('limit', limit);
    if (before) params.set('before', before);
    if (before && beforeId) params.set('beforeId', beforeId);
    if (withQuiz) params.set('withQuiz', '1');
    const query = params.toString();
    return this.request(`/messages/user/${userId}${query ? `?${query}` : ''}`);
  }

  // quizSince: the quizSyncAt of the previous answer; the server then also
  // returns quizUpdates, the quizzes of older messages that changed since.
  async getThreadMessages(threadId, { limit, before, beforeId, after, afterId, quizSince } = {}) {
    const params = new URLSearchParams();
    if (limit) params.set('limit', limit);
    if (before) params.set('before', before);
    if (before && beforeId) params.set('beforeId', beforeId);
    if (after) params.set('after', after);
    if (after && afterId) params.set('afterId', afterId);
    if (quizSince) params.set('quizSince', quizSince);
    const query = params.toString();
    return this.request(`/messages/thread/${threadId}${query ? `?${query}` : ''}`);
  }

  async getUserThreads(userId, { limit, before, beforeId, withQuiz } = {}) {
    const params = new URLSearchParams();
    if (limit) params.set('limit', limit);
    if (before) params.set('before', before);
    if (before && beforeId) params.set('beforeId', beforeId);
    if (withQuiz) params.set('withQuiz', '1');
    const query = params.toString();
    return this.request(`/threads/user/${userId}${query ? `?${query}` : ''}`);
  }

  // One conversation / one approval request by id — for push taps and e-mail
  // links. Both answer 404 to anyone who is not on it.
  async getThread(threadId) {
    return this.request(`/threads/${threadId}`);
  }

  async getApprovalMessage(messageId) {
    return this.request(`/messages/approval/${messageId}`);
  }

  async createThread(data) {
    return this.request('/threads/create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async markMessageAsRead(messageId) {
    return this.request(`/messages/${messageId}/mark-read`, {
      method: 'PATCH',
    });
  }

  async getUnreadCount(userId) {
    return this.request(`/messages/unread-count/${userId}`);
  }

  async getUnreadByThread(userId) {
    return this.request(`/messages/unread-by-thread/${userId}`);
  }

  async markThreadAsRead(threadId) {
    return this.request(`/messages/thread/${threadId}/mark-all-read`, {
      method: 'PATCH',
    });
  }

  async getPendingApprovalCount(userId) {
    return this.request(`/messages/approvals/unread-count/${userId}`);
  }

  // Combined polling endpoint – fetches unreadCount, unreadByThread, and pendingApprovalCount in ONE call
  async getPollingStatus(userId) {
    return this.request(`/messages/polling-status/${userId}`);
  }

  // Pin/unpin a thread to its parent participants' dashboards (creator/co-owner only).
  // displayMode: 'compact' (one-liner) or 'full' (snapshot first message on the card).
  async pinThread(threadId, pinned, displayMode) {
    return this.request(`/threads/${threadId}/pin`, {
      method: 'PATCH',
      body: JSON.stringify({ pinned, displayMode }),
    });
  }

  // Pin/unpin an approval request to its parent recipients' dashboards (sender/co-author only).
  async pinApproval(messageId, pinned, displayMode) {
    return this.request(`/messages/${messageId}/pin`, {
      method: 'PATCH',
      body: JSON.stringify({ pinned, displayMode }),
    });
  }

  // Pinned items (threads + approvals) visible on THIS parent's dashboard
  async getPinnedItems(userId) {
    return this.request(`/messages/pinned/${userId}`);
  }

  // ============= MESSAGING ARCHIVE (teachers/admins) =============

  // Archive (archived=true) or restore (archived=false) a thread.
  // Server allows the creator, a co-owner, or — for parent-started threads —
  // any teacher/admin participant. The flag is shared across all participants.
  async archiveThread(threadId, archived = true) {
    return this.request(`/threads/archive/${threadId}`, {
      method: 'POST',
      body: JSON.stringify({ archived }),
    });
  }

  // Archive/restore an approval request (sender/co-author only).
  async archiveApproval(messageId, archived = true) {
    return this.request(`/messages/${messageId}/archive`, {
      method: 'PATCH',
      body: JSON.stringify({ archived }),
    });
  }

  // Archived threads visible to THIS user (capped list, newest archive first)
  async getArchivedThreads(userId, { search, before, beforeId, limit } = {}) {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (before) params.set('before', before);
    if (before && beforeId) params.set('beforeId', beforeId);
    if (limit) params.set('limit', limit);
    const query = params.toString();
    return this.request(`/threads/user/${userId}/archived${query ? `?${query}` : ''}`);
  }

  // Archived approval requests visible to THIS user (capped list)
  async getArchivedApprovals(userId, { search, before, beforeId, limit } = {}) {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (before) params.set('before', before);
    if (before && beforeId) params.set('beforeId', beforeId);
    if (limit) params.set('limit', limit);
    const query = params.toString();
    return this.request(`/messages/approvals/archived/${userId}${query ? `?${query}` : ''}`);
  }

  // ============= PARENT-SPECIFIC APIS =============
  
  // Parent approval response
  async respondToApproval(messageId, status) {
    return this.request(`/messages/approval/${messageId}/respond`, {
      method: 'POST',
      body: JSON.stringify({ status }),
    });
  }

  // Absence Requests API - Parent endpoints
  // childId narrows the list (and its counts) to one child — the parent absence
  // card's child selector governs both its calendar and its history list.
  async getParentAbsenceRequests(parentId, { status, limit, before, beforeId, childId } = {}) {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (limit) params.set('limit', limit);
    if (before) params.set('before', before);
    if (beforeId) params.set('beforeId', beforeId);
    if (childId) params.set('childId', childId);
    const query = params.toString();
    return this.request(`/absence-requests/parent/${parentId}${query ? `?${query}` : ''}`);
  }

  async createAbsenceRequest(data) {
    return this.request('/absence-requests/create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAbsenceRequest(id, data) {
    return this.request(`/absence-requests/update/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteAbsenceRequest(id) {
    return this.request(`/absence-requests/delete/${id}`, {
      method: 'DELETE',
    });
  }

  // Request a change ('modify') or cancellation ('cancel') of an APPROVED absence.
  // data: { originalRequestId, changeType, startDate?, endDate?, timeSlot?, reason? }
  async createAbsenceChangeRequest(data) {
    return this.request('/absence-requests/change-request', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ============= TEACHER ABSENCE REQUEST APIS =============
  
  // groupId/from/to narrow the list to the calendar's group and month; `search`
  // matches a child's name and deliberately ignores from/to, so a lookup spans
  // every month. groupId can only narrow — the server drops one the teacher
  // does not teach rather than honouring it.
  async getTeacherAbsenceRequests(teacherId, { status, limit, before, beforeId, groupId, from, to, search } = {}) {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (limit) params.set('limit', limit);
    if (before) params.set('before', before);
    if (beforeId) params.set('beforeId', beforeId);
    if (groupId) params.set('groupId', groupId);
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (search) params.set('search', search);
    const query = params.toString();
    return this.request(`/absence-requests/teacher/${teacherId}${query ? `?${query}` : ''}`);
  }

  async getGroupAbsenceRequests(groupId, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/absence-requests/group/${groupId}${queryString ? '?' + queryString : ''}`);
  }

  async approveAbsenceRequest(requestId, note = '') {
    return this.request(`/absence-requests/${requestId}/decide`, {
      method: 'POST',
      body: JSON.stringify({ action: 'approved', note }),
    });
  }

  async rejectAbsenceRequest(requestId, note = '') {
    return this.request(`/absence-requests/${requestId}/decide`, {
      method: 'POST',
      body: JSON.stringify({ action: 'rejected', note }),
    });
  }

  async getAbsenceCalendar(groupId, month, year) {
    return this.request(`/absence-requests/calendar/group/${groupId}?month=${month}&year=${year}`);
  }

  // Parent: absence calendar for a single child (month view; includes timeSlot)
  async getChildAbsenceCalendar(childId, month, year) {
    return this.request(`/absence-requests/calendar/child/${childId}?month=${month}&year=${year}`);
  }

  async getPendingAbsenceCount(teacherId) {
    return this.request(`/absence-requests/pending-count/${teacherId}`);
  }

  // Archived absence requests visible to THIS user (teacher: their groups;
  // admin: whole tenant) — the same people who may archive and restore them.
  async getArchivedAbsenceRequests({ search, before, beforeId, limit } = {}) {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (before) params.set('before', before);
    if (beforeId) params.set('beforeId', beforeId);
    if (limit) params.set('limit', limit);
    const query = params.toString();
    return this.request(`/absence-requests/archived${query ? `?${query}` : ''}`);
  }

  // Shared flag, like the messaging archive: archiving hides the request from
  // the parent and from the other teachers of the group until someone restores it.
  async archiveAbsenceRequest(requestId, archived = true) {
    return this.request(`/absence-requests/${requestId}/archive`, {
      method: 'POST',
      body: JSON.stringify({ archived }),
    });
  }

  async restoreAbsenceRequest(requestId) {
    return this.request(`/absence-requests/archived/${requestId}/restore`, {
      method: 'POST',
    });
  }

  // ============= ADMIN ALIAS MANAGEMENT =============
  
  async getAdminUserAlias(adminId) {
    return this.request(`/users/admin-alias/${adminId}`);
  }

  async syncAdminToUserAlias(adminId, data) {
    return this.request(`/users/sync-admin-alias/${adminId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async isAdminAlias(userId) {
    return this.request(`/users/${userId}/is-admin-alias`);
  }

  async getAllUsersWithAliases() {
    return this.request('/users?includeAdmins=true');
  }

// ============= ADMIN VALIDATION STATS =============
 async getAdminValidationStats() {
    return this.request('/users/validation-stats');
  }

  async getDirectorValidationStats() {
    return this.request('/users/director-validation-stats');
  }

  // ============= OCCUPANCY MANAGEMENT =============

  // Get occupancy for kindergarten for a specific week
  async getKindergartenOccupancy(kindergartenId, weekStart) {
    return this.request(`/occupancy/kindergarten/${kindergartenId}/week/${weekStart}`);
  }

  // Get occupancy for a group for a specific week
  async getGroupOccupancy(groupId, weekStart) {
    return this.request(`/occupancy/group/${groupId}/week/${weekStart}`);
  }

  // Get capacity settings for a group
  async getGroupCapacity(groupId) {
    return this.request(`/occupancy/group/${groupId}/capacity`);
  }

  // Update capacity settings for a group
  async updateGroupCapacity(groupId, data) {
    return this.request(`/occupancy/group/${groupId}/capacity`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // Get all children with booking information (with filters)
  async getChildrenBookings(filters = {}) {
    const queryString = new URLSearchParams(filters).toString();
    return this.request(`/occupancy/children${queryString ? '?' + queryString : ''}`);
  }

  // Get booking details for a specific child
  async getChildBooking(childId) {
    return this.request(`/occupancy/child/${childId}/booking`);
  }

  // Update booking for a child
  async updateChildBooking(childId, data) {
    return this.request(`/occupancy/child/${childId}/booking`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // Get booking history for a child
  async getChildBookingHistory(childId) {
    return this.request(`/occupancy/child/${childId}/history`);
  }

  // Advanced search/filter for children
  async searchChildren(filters) {
    return this.request('/occupancy/search', {
      method: 'POST',
      body: JSON.stringify(filters),
    });
  }

  // ============= CAPACITY EXCEPTIONS =============

  // Create a capacity exception for a specific day/time slot
  async createCapacityException(data) {
    return this.request('/occupancy/exception', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Get all exceptions for a group in a specific week
  async getGroupWeekExceptions(groupId, weekStart) {
    return this.request(`/occupancy/group/${groupId}/exceptions/week/${weekStart}`);
  }

  // Get all exceptions for a kindergarten in a specific week
  async getKindergartenWeekExceptions(kindergartenId, weekStart) {
    return this.request(`/occupancy/kindergarten/${kindergartenId}/exceptions/week/${weekStart}`);
  }

  // Get a specific exception by ID
  async getCapacityException(exceptionId) {
    return this.request(`/occupancy/exception/${exceptionId}`);
  }

  // Update a capacity exception
  async updateCapacityException(exceptionId, data) {
    return this.request(`/occupancy/exception/${exceptionId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // Delete (soft delete) a capacity exception
  async deleteCapacityException(exceptionId) {
    return this.request(`/occupancy/exception/${exceptionId}`, {
      method: 'DELETE',
    });
  }

  // ============= FILE UPLOADS =============
  async uploadFiles(files) {
    // ✅ Updated to include credentials and app identifier
    const formData = new FormData();
    
    for (const file of files) {
      formData.append('files', file);
    }

    try {
      const appId = getAppIdentifier();
      
      const response = await fetch(`${API_BASE}/uploads`, {
        method: 'POST',
        credentials: 'include', // ✅ Critical for sessions!
        headers: {
          'X-App-Identifier': appId || '', // ✅ Tell backend it's nidoux
          // Note: Don't set Content-Type for FormData - browser sets it automatically with boundary
        },
        body: formData,
      });

      if (!response.ok) {
        try {
          const errorData = await response.json();
          const error = new Error(errorData.message || 'File upload request failed');
          error.status = response.status;
          throw error;
        } catch (jsonError) {
          if (jsonError instanceof Error && jsonError.status) {
            throw jsonError;
          }
          const error = new Error(`Upload failed with status ${response.status}`);
          error.status = response.status;
          throw error;
        }
      }

      return await response.json();
    } catch (error) {
      // Only log server errors (5xx), not client/validation errors (4xx)
      if (error.status && error.status >= 500) {
        console.error('API Upload Server Error:', error);
      }
      throw error;
    }
  }

  // ============= INVOICING MODULE APIS =============

  // Invoice Configuration API
  async getInvoiceConfigurations(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/invoice-configurations${queryString ? '?' + queryString : ''}`);
  }

  async getCurrentInvoiceConfiguration(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/invoice-configurations/current${queryString ? '?' + queryString : ''}`);
  }

  async getInvoiceValidationStats(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/invoice-validation/stats${queryString ? '?' + queryString : ''}`);
  }

  async getInvoiceConfiguration(id, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/invoice-configurations/${id}${queryString ? '?' + queryString : ''}`);
  }

  async createInvoiceConfiguration(data) {
    return this.request('/invoice-configurations/create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateInvoiceConfiguration(id, data) {
    return this.request(`/invoice-configurations/update/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteInvoiceConfiguration(id, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/invoice-configurations/delete/${id}${queryString ? '?' + queryString : ''}`, {
      method: 'DELETE',
    });
  }

  // Payment Slip API
  async getPaymentSlips(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/payment-slips${queryString ? '?' + queryString : ''}`);
  }

  async getPaymentSlip(id, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/payment-slips/${id}${queryString ? '?' + queryString : ''}`);
  }

  async createPaymentSlip(data) {
    return this.request('/payment-slips/create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePaymentSlip(id, data) {
    return this.request(`/payment-slips/update/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deletePaymentSlip(id, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/payment-slips/delete/${id}${queryString ? '?' + queryString : ''}`, {
      method: 'DELETE',
    });
  }

  async testPaymentReference(slipId, invoiceNumber, kindergartenId) {
    return this.request(`/payment-slips/${slipId}/test-reference`, {
      method: 'POST',
      body: JSON.stringify({ invoiceNumber, kindergartenId }),
    });
  }

  // Document Layout API
  async getDocumentLayouts(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/document-layouts${queryString ? '?' + queryString : ''}`);
  }

  async getDocumentLayout(id, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/document-layouts/${id}${queryString ? '?' + queryString : ''}`);
  }

  async createDocumentLayout(data) {
    return this.request('/document-layouts/create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateDocumentLayout(id, data) {
    return this.request(`/document-layouts/update/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteDocumentLayout(id, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/document-layouts/delete/${id}${queryString ? '?' + queryString : ''}`, {
      method: 'DELETE',
    });
  }

  // Logo Upload API
  async uploadLogo(file) {
    const formData = new FormData();
    formData.append('file', file);

    const appId = getAppIdentifier();

    const response = await fetch(`${API_BASE}/uploads/logo`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'X-App-Identifier': appId || ''
        // Don't set Content-Type - browser sets it with boundary for FormData
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json();
      const error = new Error(errorData.message || `Upload failed with status ${response.status}`);
      throw error;
    }

    return await response.json();
  }

  // Invoice API
  async getInvoices(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/invoices${queryString ? '?' + queryString : ''}`);
  }

  async getInvoice(id, kindergartenId) {
    return this.request(`/invoices/${id}?kindergartenId=${kindergartenId}`);
  }

  async createInvoice(data) {
    return this.request('/invoices/create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateInvoice(id, data) {
    return this.request(`/invoices/update/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async finalizeInvoice(id, kindergartenId) {
    return this.request(`/invoices/update/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status: 'sent', kindergartenId }),
    });
  }

  async cancelInvoice(id, reason, kindergartenId) {
    return this.request(`/invoices/cancel/${id}`, {
      method: 'POST',
      body: JSON.stringify({ reason, kindergartenId }),
    });
  }

  async deleteInvoice(id, kindergartenId) {
    return this.request(`/invoices/delete/${id}?kindergartenId=${kindergartenId}`, {
      method: 'DELETE',
    });
  }

  async getOpenItems() {
    return this.request('/invoices/reports/open-items');
  }

  async getInvoiceStatistics(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/invoices/reports/statistics${queryString ? '?' + queryString : ''}`);
  }

  async getInvoiceSystemHealth(kindergartenId) {
    return this.request(`/invoices/system-health?kindergartenId=${kindergartenId}`);
  }

  // Invoice Run API
  async getInvoiceRuns(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/invoice-runs${queryString ? '?' + queryString : ''}`);
  }

  async getRecentInvoiceRuns(limit = 10) {
    return this.request(`/invoice-runs/recent?limit=${limit}`);
  }

  async getInvoiceRun(id) {
    return this.request(`/invoice-runs/${id}`);
  }

  async createInvoiceRun(data) {
    return this.request('/invoice-runs/create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async executeInvoiceRun(data) {
    return this.request('/invoice-runs/execute', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getInvoiceRunByPeriod(period, kindergartenId = null) {
    const params = kindergartenId ? `?kindergartenId=${kindergartenId}` : '';
    return this.request(`/invoice-runs/period/${period}${params}`);
  }

  async deleteInvoiceRun(id) {
    return this.request(`/invoice-runs/delete/${id}`, {
      method: 'DELETE',
    });
  }

  async resendRunEmails(runId) {
    return this.request(`/invoice-runs/${runId}/resend-emails`, {
      method: 'POST',
    });
  }

  // Dunning Run API
  async getDunningRuns(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/dunning-runs${queryString ? '?' + queryString : ''}`);
  }

  async getRecentDunningRuns(limit = 10) {
    return this.request(`/dunning-runs/recent?limit=${limit}`);
  }

  async getDunningRun(id) {
    return this.request(`/dunning-runs/${id}`);
  }

  async executeDunningRun(data = {}) {
    return this.request('/dunning-runs/execute', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getDunningStatistics(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/dunning-runs/reports/statistics${queryString ? '?' + queryString : ''}`);
  }

  async deleteDunningRun(id) {
    return this.request(`/dunning-runs/delete/${id}`, {
      method: 'DELETE',
    });
  }

  // Payment API
  async getPayments(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/payments${queryString ? '?' + queryString : ''}`);
  }

  async getPaymentsReport(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/payments/reports/payments${queryString ? '?' + queryString : ''}`);
  }

  async getUnmatchedPayments(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/payments/reports/unmatched${queryString ? '?' + queryString : ''}`);
  }

  async getPaymentTotals(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/payments/reports/totals${queryString ? '?' + queryString : ''}`);
  }

  async createPayment(data) {
    return this.request('/payments/create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async recordManualPayment(data) {
    return this.request('/payments/manual', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Alias for recordManualPayment (used by PaymentsSection)
  async recordPayment(data) {
    return this.recordManualPayment(data);
  }

  /**
   * Preview eligible invoices for an invoice run
   *
   * @returns {Promise<Object>} Preview data with billability information
   *
   * Response structure:
   * {
   *   invoices: [
   *     {
   *       _id: string,
   *       invoiceNumber: string,
   *       totalAmount: number,
   *       isBillable: boolean,
   *       nonBillableReasons: string[],
   *       billableLineItemCount: number,
   *       nonBillableLineItemCount: number,
   *       hasBlockingIssues: boolean,
   *       parentId: { name: string, email: string },
   *       childId: { firstName: string, lastName: string, birthDate: Date },
   *       ...
   *     }
   *   ],
   *   count: number,
   *   billableCount: number,
   *   nonBillableCount: number,
   *   summary: {
   *     total: number,
   *     billable: number,
   *     nonBillable: number,
   *     readyToSend: number
   *   }
   * }
   */
  async previewInvoiceRun() {
    return this.request('/invoice-runs/preview');
  }

  /**
   * Helper: Filter preview results to only billable invoices
   *
   * @param {Object} previewData - Result from previewInvoiceRun()
   * @returns {Array} Array of billable invoices only
   */
  filterBillableInvoices(previewData) {
    if (!previewData || !previewData.invoices) {
      return [];
    }
    return previewData.invoices.filter(invoice => invoice.isBillable === true);
  }

  /**
   * Helper: Filter preview results to only non-billable invoices
   *
   * @param {Object} previewData - Result from previewInvoiceRun()
   * @returns {Array} Array of non-billable invoices with reasons
   */
  filterNonBillableInvoices(previewData) {
    if (!previewData || !previewData.invoices) {
      return [];
    }
    return previewData.invoices.filter(invoice => invoice.isBillable === false);
  }

  /**
   * Helper: Check if a line item is valid and billable
   *
   * @param {Object} lineItem - Line item to validate
   * @returns {Object} { isValid: boolean, errors: string[] }
   */
  validateLineItem(lineItem) {
    const errors = [];

    if (!lineItem.description || lineItem.description.trim() === '') {
      errors.push('Description is required');
    }

    if (lineItem.quantity === null || lineItem.quantity === undefined || lineItem.quantity <= 0) {
      errors.push('Quantity must be greater than zero');
    }

    if (lineItem.unitPrice === null || lineItem.unitPrice === undefined || lineItem.unitPrice < 0) {
      errors.push('Unit price must be zero or greater');
    }

    if (lineItem.totalPrice === null || lineItem.totalPrice === undefined || lineItem.totalPrice < 0) {
      errors.push('Total price must be zero or greater');
    }

    // Check calculation
    if (lineItem.quantity && lineItem.unitPrice !== null && lineItem.unitPrice !== undefined) {
      const expectedTotal = lineItem.quantity * lineItem.unitPrice;
      const diff = Math.abs(expectedTotal - (lineItem.totalPrice || 0));

      if (diff > 0.01) {
        errors.push(`Total price should be ${expectedTotal.toFixed(2)}`);
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // ============= INVOICE EMAIL FUNCTIONALITY =============

  // Send invoice email
  async sendInvoiceEmail(invoiceId, options = {}, kindergartenId) {
    return this.request(`/invoices/${invoiceId}/send-email`, {
      method: 'POST',
      body: JSON.stringify({ ...options, kindergartenId }), // { emailType: 'invoice', recipientEmail: 'optional@email.com', kindergartenId }
    });
  }

  // Get invoice email log
  async getInvoiceEmailLog(invoiceId, kindergartenId) {
    return this.request(`/invoices/${invoiceId}/email-log?kindergartenId=${kindergartenId}`);
  }

  // Download invoice PDF
  async downloadInvoicePDF(invoiceId, kindergartenId) {
    const appId = getAppIdentifier();

    // Use fetch directly to handle binary data
    const response = await fetch(`${API_BASE}/invoices/${invoiceId}/download?kindergartenId=${kindergartenId}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'X-App-Identifier': appId || '',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to download invoice PDF');
    }

    // Return the blob so caller can handle download
    const blob = await response.blob();
    return blob;
  }

  // ============= INVOICE REVISION FUNCTIONALITY =============

  /**
   * Create a new revision of an existing invoice
   *
   * @param {string} invoiceId - ID of invoice to revise
   * @param {Object} changes - Changes to apply to the new revision
   * @param {string} reason - Reason for creating the revision
   * @param {string} kindergartenId - Kindergarten ID
   * @returns {Promise<Object>} - { message, originalInvoice, newRevision }
   */
  async createInvoiceRevision(invoiceId, changes, reason, kindergartenId) {
    return this.request(`/invoices/${invoiceId}/create-revision`, {
      method: 'POST',
      body: JSON.stringify({ changes, reason, kindergartenId }),
    });
  }

  /**
   * Get all revisions for an invoice
   *
   * @param {string} invoiceId - ID of any revision
   * @param {string} kindergartenId - Kindergarten ID
   * @returns {Promise<Object>} - { invoiceId, revisions, count }
   */
  async getInvoiceRevisions(invoiceId, kindergartenId) {
    return this.request(`/invoices/${invoiceId}/revisions?kindergartenId=${kindergartenId}`);
  }

  /**
   * Get the current (latest) revision of an invoice
   *
   * @param {string} invoiceId - ID of any revision
   * @returns {Promise<Object>} - Current revision object
   */
  async getCurrentInvoiceRevision(invoiceId) {
    return this.request(`/invoices/${invoiceId}/current-revision`);
  }

  /**
   * Compare two revisions and get differences
   *
   * @param {string} invoiceId1 - ID of first revision
   * @param {string} invoiceId2 - ID of second revision
   * @returns {Promise<Object>} - Comparison object with changes
   */
  async compareInvoiceRevisions(invoiceId1, invoiceId2) {
    return this.request(`/invoices/${invoiceId1}/compare/${invoiceId2}`);
  }

  /**
   * Get revision statistics for an invoice
   *
   * @param {string} invoiceId - ID of any revision
   * @returns {Promise<Object>} - Statistics about revisions
   */
  async getInvoiceRevisionStats(invoiceId) {
    return this.request(`/invoices/${invoiceId}/revision-stats`);
  }

  /**
   * Check if an invoice can be revised
   *
   * @param {string} invoiceId - ID of invoice to check
   * @param {string} kindergartenId - Kindergarten ID
   * @returns {Promise<Object>} - { canRevise: boolean, reason: string }
   */
  async canReviseInvoice(invoiceId, kindergartenId) {
    return this.request(`/invoices/${invoiceId}/can-revise?kindergartenId=${kindergartenId}`);
  }

  /**
   * Get related split invoices for a given invoice
   *
   * @param {string} invoiceId - ID of the split invoice
   * @param {string} kindergartenId - Kindergarten ID
   * @returns {Promise<Object>} - { invoiceId, isSplitInvoice, splitPercentage, relatedInvoices }
   */
  async getRelatedSplitInvoices(invoiceId, kindergartenId) {
    return this.request(`/invoices/${invoiceId}/related-splits?kindergartenId=${kindergartenId}`);
  }

  /**
   * Validate billing recipients for a child
   *
   * @param {string} childId - ID of the child
   * @returns {Promise<Object>} - { isValid: boolean, errors: Array }
   */
  async validateBillingRecipients(childId) {
    return this.request(`/invoices/children/${childId}/validate-billing`);
  }

  /**
   * Download multiple invoices as ZIP
   */
  async downloadInvoicesBulk(invoiceIds) {
    const baseUrl = this.getBaseUrl();
    const response = await fetch(`${baseUrl}/invoices/download-bulk`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ invoiceIds })
    });
    if (!response.ok) throw new Error('Failed to download invoices');
    return response.blob();
  }

  /**
   * Download invoices by filter as ZIP
   */
  async downloadInvoicesFiltered(filters) {
    const baseUrl = this.getBaseUrl();
    const response = await fetch(`${baseUrl}/invoices/download-filtered`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(filters)
    });
    if (!response.ok) throw new Error('Failed to download invoices');
    return response.blob();
  }

  /**
   * Download invoice run as ZIP
   */
  async downloadInvoiceRunZip(runId) {
    const baseUrl = this.getBaseUrl();
    const response = await fetch(`${baseUrl}/invoice-runs/${runId}/download-zip`, {
      method: 'GET',
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Failed to download invoice run');
    return response.blob();
  }

  // Invoice Text Templates
  async getInvoiceTextTemplates(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/invoice-text-templates${queryString ? '?' + queryString : ''}`);
  }

  async getInvoiceTextTemplate(id, kindergartenId) {
    const params = kindergartenId ? `?kindergartenId=${kindergartenId}` : '';
    return this.request(`/invoice-text-templates/${id}${params}`);
  }

  async getActiveInvoiceTextTemplate(textType, language = 'de') {
    return this.request(`/invoice-text-templates/active/${textType}?language=${language}`);
  }

  async createInvoiceTextTemplate(data) {
    return this.request('/invoice-text-templates/create', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateInvoiceTextTemplate(id, data) {
    return this.request(`/invoice-text-templates/update/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  async deleteInvoiceTextTemplate(id) {
    return this.request(`/invoice-text-templates/delete/${id}`, {
      method: 'DELETE'
    });
  }

  async previewInvoiceTextTemplate(id, sampleData) {
    return this.request(`/invoice-text-templates/${id}/preview`, {
      method: 'POST',
      body: JSON.stringify(sampleData)
    });
  }

  async seedDefaultInvoiceTextTemplates() {
    return this.request('/invoice-text-templates/seed-defaults', {
      method: 'POST',
      body: JSON.stringify({})
    });
  }

  // Accounting Exports
  async exportDebtors(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const baseUrl = this.getBaseUrl();
    const response = await fetch(`${baseUrl}/accounting-exports/debtors${queryString ? '?' + queryString : ''}`, {
      method: 'GET',
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Failed to export debtors');
    return response.blob();
  }

  async exportOpenItemsAccounting(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const baseUrl = this.getBaseUrl();
    const response = await fetch(`${baseUrl}/accounting-exports/open-items${queryString ? '?' + queryString : ''}`, {
      method: 'GET',
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Failed to export open items');
    return response.blob();
  }

  async getFinancialSummary(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/accounting-exports/financial-summary${queryString ? '?' + queryString : ''}`);
  }

  async exportCompleteAccounting(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const baseUrl = this.getBaseUrl();
    const response = await fetch(`${baseUrl}/accounting-exports/complete-export${queryString ? '?' + queryString : ''}`, {
      method: 'GET',
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Failed to export accounting data');
    return response.blob();
  }

  async previewDunningRun(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/dunning-runs/preview${queryString ? '?' + queryString : ''}`);
  }

  async cancelPayment(id, reason) {
    return this.request(`/payments/cancel/${id}`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  /**
   * Import payment file (ESR/BESR/CAMT.054)
   * File is uploaded to Cloudinary and then processed
   *
   * @param {File} file - Payment file to import
   * @returns {Promise<Object>} Import results with matched/unmatched payments
   *
   * @example
   * const file = event.target.files[0];
   * const result = await api.importPaymentFile(file);
   * console.log(`Matched: ${result.results.matched}, Unmatched: ${result.results.unmatched}`);
   */
  async importPaymentFile(file, format = 'auto') {
    const formData = new FormData();
    formData.append('paymentFile', file); // Field name must match backend: 'paymentFile'
    formData.append('format', format); // Specify import format: auto, camt054, esr, iso20022, csv

    const baseUrl = this.getBaseUrl();
    const response = await fetch(`${baseUrl}/payments/import-file`, {
      method: 'POST',
      credentials: 'include',
      body: formData, // Send FormData directly, browser sets Content-Type with boundary
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Import failed' }));
      throw new Error(error.message || 'Failed to import payment file');
    }

    return response.json();
  }

  // Legacy method (the old InvoicingManager builds its own FormData with a
  // 'file' field) - same endpoint as importPaymentFile.
  async importPayments(formData) {
    return this.importPaymentFile(formData.get('file') || formData.get('paymentFile'), formData.get('format') || 'auto');
  }

  async matchPaymentToInvoice(paymentId, invoiceId) {
    return this.request(`/payments/match/${paymentId}`, {
      method: 'POST',
      body: JSON.stringify({ invoiceId }),
    });
  }

  async deletePayment(id) {
    return this.request(`/payments/delete/${id}`, {
      method: 'DELETE',
    });
  }

  // ============= PREPAYMENT / OPEN ITEMS FUNCTIONALITY =============

  /**
   * Create a new prepayment record
   *
   * @param {Object} data - Prepayment data
   * @returns {Promise<Object>} - Created prepayment
   */
  async createPrepayment(data) {
    return this.request('/open-items/prepayment', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Apply a prepayment to an invoice
   *
   * @param {string} openItemId - ID of prepayment to apply
   * @param {string} invoiceId - ID of invoice to apply to
   * @param {number} amount - Amount to apply (optional)
   * @returns {Promise<Object>} - Application result
   */
  async applyPrepaymentToInvoice(openItemId, invoiceId, amount) {
    const body = amount ? { amount } : {};
    return this.request(`/open-items/${openItemId}/apply-to-invoice/${invoiceId}`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /**
   * Auto-apply all available prepayments to an invoice (FIFO)
   *
   * @param {string} invoiceId - ID of invoice
   * @returns {Promise<Object>} - Application result
   */
  async autoApplyPrepayments(invoiceId) {
    return this.request(`/open-items/auto-apply/${invoiceId}`, {
      method: 'POST',
    });
  }

  /**
   * Unapply a prepayment from an invoice (reverse application)
   *
   * @param {string} openItemId - ID of prepayment
   * @param {string} invoiceId - ID of invoice
   * @returns {Promise<Object>} - Unapplication result
   */
  async unapplyPrepaymentFromInvoice(openItemId, invoiceId) {
    return this.request(`/open-items/${openItemId}/unapply-from-invoice/${invoiceId}`, {
      method: 'POST',
    });
  }

  /**
   * Create a refund record
   *
   * @param {Object} data - Refund data
   * @returns {Promise<Object>} - Created refund
   */
  async createRefund(data) {
    return this.request('/open-items/refund', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Create a credit record
   *
   * @param {Object} data - Credit data
   * @returns {Promise<Object>} - Created credit
   */
  async createCredit(data) {
    return this.request('/open-items/credit', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Record an overpayment
   *
   * @param {Object} data - Overpayment data
   * @returns {Promise<Object>} - Created overpayment
   */
  async createOverpayment(data) {
    return this.request('/open-items/overpayment', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Mark a refund as paid out
   *
   * @param {string} refundId - ID of refund
   * @param {Date} paidOutDate - Date paid out (optional)
   * @returns {Promise<Object>} - Updated refund
   */
  async markRefundAsPaidOut(refundId, paidOutDate) {
    const body = paidOutDate ? { paidOutDate } : {};
    return this.request(`/open-items/${refundId}/mark-paid-out`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  /**
   * Get all open items with filters
   *
   * @param {Object} filters - Optional filters (type, status, parentId, hasBalance)
   * @returns {Promise<Array>} - Array of open items
   */
  async getOpenItems(filters = {}) {
    const queryString = new URLSearchParams(filters).toString();
    return this.request(`/open-items${queryString ? '?' + queryString : ''}`);
  }

  /**
   * Get open items for a specific parent
   *
   * @param {string} parentId - ID of parent
   * @param {Object} filters - Optional filters
   * @returns {Promise<Array>} - Array of open items
   */
  async getParentOpenItems(parentId, filters = {}) {
    const queryString = new URLSearchParams(filters).toString();
    return this.request(`/open-items/parent/${parentId}${queryString ? '?' + queryString : ''}`);
  }

  /**
   * Get prepayment balance for a parent
   *
   * @param {string} parentId - ID of parent
   * @returns {Promise<Object>} - { parentId, balance }
   */
  async getParentPrepaymentBalance(parentId) {
    return this.request(`/open-items/parent/${parentId}/balance`);
  }

  /**
   * Get open items summary for admin dashboard
   *
   * @param {Object} params - Optional params including kindergartenId
   * @returns {Promise<Object>} - Summary statistics
   */
  async getOpenItemsSummary(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/open-items/summary${queryString ? '?' + queryString : ''}`);
  }

  /**
   * Get a single open item
   *
   * @param {string} openItemId - ID of open item
   * @returns {Promise<Object>} - Open item object
   */
  async getOpenItem(openItemId) {
    return this.request(`/open-items/${openItemId}`);
  }

  /**
   * Delete an open item (soft delete)
   *
   * @param {string} openItemId - ID of open item
   * @returns {Promise<Object>} - Deletion result
   */
  async deleteOpenItem(openItemId) {
    return this.request(`/open-items/${openItemId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Scan for existing overpayments (invoices where paidAmount > totalAmount)
   *
   * @returns {Promise<Object>} - Scan results with summary and invoice details
   */
  async scanForOverpayments() {
    return this.request('/open-items/scan/overpayments');
  }

  /**
   * Fix overpayments by creating OpenItem records for invoices without them
   *
   * @returns {Promise<Object>} - Fix results
   */
  async fixOverpayments() {
    return this.request('/open-items/fix-overpayments', {
      method: 'POST',
    });
  }

  async getAdminValidationStats() {
    return this.request('/users/validation-stats');
  }

  // Helper method to get base URL
  getBaseUrl() {
    return API_BASE;
  }

  // ============= EXPORT & REPORTING APIS =============

  async exportInvoices(format, filters = {}) {
    const queryString = new URLSearchParams({ format, ...filters }).toString();
    const baseUrl = this.getBaseUrl();
    const appId = getAppIdentifier();

    const response = await fetch(`${baseUrl}/invoices/export?${queryString}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'X-App-Identifier': appId || '',
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Export failed' }));
      throw new Error(error.message || 'Export failed');
    }

    if (format === 'csv') {
      return await response.text();
    } else if (format === 'pdf') {
      return await response.blob();
    }
  }

  async exportPayments(format, filters = {}) {
    const queryString = new URLSearchParams({ format, ...filters }).toString();
    const baseUrl = this.getBaseUrl();
    const appId = getAppIdentifier();

    const response = await fetch(`${baseUrl}/payments/export?${queryString}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'X-App-Identifier': appId || '',
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Export failed' }));
      throw new Error(error.message || 'Export failed');
    }

    if (format === 'csv') {
      return await response.text();
    } else if (format === 'pdf') {
      return await response.blob();
    }
  }

  async exportOpenItems(format, filters = {}) {
    const queryString = new URLSearchParams({ format, ...filters }).toString();
    const baseUrl = this.getBaseUrl();
    const appId = getAppIdentifier();

    const response = await fetch(`${baseUrl}/invoices/reports/open-items/export?${queryString}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'X-App-Identifier': appId || '',
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Export failed' }));
      throw new Error(error.message || 'Export failed');
    }

    if (format === 'csv') {
      return await response.text();
    } else if (format === 'pdf') {
      return await response.blob();
    }
  }

  async getOpenItemsReport(filters = {}) {
    const queryString = new URLSearchParams(filters).toString();
    return this.request(`/invoices/reports/open-items-detailed${queryString ? '?' + queryString : ''}`);
  }

  // ============================================================
  // PERSONNEL PLANNING API
  // ============================================================

  // Employment Contracts
  async getEmploymentContracts(kindergartenId) {
    return this.request(`/employment-contracts/kindergarten/${kindergartenId}`);
  }

  async getEmploymentContract(id) {
    return this.request(`/employment-contracts/${id}`);
  }

  async getPersonnelContracts(carePersonnelId) {
    return this.request(`/employment-contracts/personnel/${carePersonnelId}`);
  }

  async createEmploymentContract(data) {
    return this.request('/employment-contracts/create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateEmploymentContract(id, data) {
    return this.request(`/employment-contracts/update/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteEmploymentContract(id) {
    return this.request(`/employment-contracts/delete/${id}`, {
      method: 'DELETE',
    });
  }

  // Shifts
  async getShifts(kindergartenId) {
    return this.request(`/shifts/kindergarten/${kindergartenId}`);
  }

  async getShift(id) {
    return this.request(`/shifts/${id}`);
  }

  async createShift(data) {
    return this.request('/shifts/create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateShift(id, data) {
    return this.request(`/shifts/update/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteShift(id) {
    return this.request(`/shifts/delete/${id}`, {
      method: 'DELETE',
    });
  }

  // Duty Assignments
  async getDutyAssignmentsForWeek(kindergartenId, weekStart) {
    return this.request(`/duty-assignments/week/${kindergartenId}/${weekStart}`);
  }

  async getDutyAssignmentsForRange(kindergartenId, startDate, endDate) {
    return this.request(`/duty-assignments/range/${kindergartenId}/${startDate}/${endDate}`);
  }

  async getDutyAssignmentsForGroupWeek(kindergartenId, groupId, weekStart) {
    return this.request(`/duty-assignments/week/${kindergartenId}/${groupId}/${weekStart}`);
  }

  async getDutyAssignmentsForPersonnel(carePersonnelId, weekStart) {
    return this.request(`/duty-assignments/personnel/${carePersonnelId}/week/${weekStart}`);
  }

  async getDutyAssignment(id) {
    return this.request(`/duty-assignments/${id}`);
  }

  // Teacher-specific: Get own schedule for a week
  async getMySchedule(weekStart) {
    return this.request(`/duty-assignments/my-schedule/week/${weekStart}`);
  }

  async createDutyAssignment(data) {
    return this.request('/duty-assignments/create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createDutyAssignmentRange(data) {
    return this.request('/duty-assignments/create-range', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async checkDutyAssignmentConflicts(data) {
    return this.request('/duty-assignments/check-conflicts-range', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async bulkCreateDutyAssignments(assignments) {
    return this.request('/duty-assignments/bulk-create', {
      method: 'POST',
      body: JSON.stringify({ assignments }),
    });
  }

  async updateDutyAssignment(id, data) {
    return this.request(`/duty-assignments/update/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteDutyAssignment(id) {
    return this.request(`/duty-assignments/delete/${id}`, {
      method: 'DELETE',
    });
  }

  // Personnel Needs
  async getPersonnelNeeds(kindergartenId) {
    return this.request(`/personnel-needs/kindergarten/${kindergartenId}`);
  }

  async getPersonnelNeedsForGroup(groupId) {
    return this.request(`/personnel-needs/group/${groupId}`);
  }

  async getPersonnelNeedsForDate(groupId, date) {
    return this.request(`/personnel-needs/group/${groupId}/date/${date}`);
  }

  async getPersonnelNeed(id) {
    return this.request(`/personnel-needs/${id}`);
  }

  async createPersonnelNeed(data) {
    return this.request('/personnel-needs/create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async bulkUpdatePersonnelNeeds(kindergartenId, groupId, weeklyNeeds) {
    return this.request('/personnel-needs/bulk-update', {
      method: 'POST',
      body: JSON.stringify({ kindergartenId, groupId, weeklyNeeds }),
    });
  }

  async updatePersonnelNeed(id, data) {
    return this.request(`/personnel-needs/update/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deletePersonnelNeed(id) {
    return this.request(`/personnel-needs/delete/${id}`, {
      method: 'DELETE',
    });
  }

  // Personnel Planning (Supervision Ratios)
  async getSupervisionRatios(kindergartenId, weekStart) {
    return this.request(`/personnel-planning/supervision-ratio/${kindergartenId}/${weekStart}`);
  }

  async getPersonnelPlanningOverview(kindergartenId, weekStart) {
    return this.request(`/personnel-planning/overview/${kindergartenId}/${weekStart}`);
  }

  async getStaffingGaps(kindergartenId, weekStart) {
    return this.request(`/personnel-planning/gaps/${kindergartenId}/${weekStart}`);
  }

  // Employee Absences
  async getEmployeeAbsences(kindergartenId, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/employee-absences/kindergarten/${kindergartenId}${queryString ? '?' + queryString : ''}`);
  }

  async getEmployeeAbsencesForPersonnel(carePersonnelId, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/employee-absences/personnel/${carePersonnelId}${queryString ? '?' + queryString : ''}`);
  }

  async getEmployeeAbsencesForDateRange(kindergartenId, startDate, endDate) {
    return this.request(`/employee-absences/date-range/${kindergartenId}/${startDate}/${endDate}`);
  }

  async createEmployeeAbsence(data) {
    return this.request('/employee-absences/create', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateEmployeeAbsence(id, data) {
    return this.request(`/employee-absences/update/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  async deleteEmployeeAbsence(id) {
    return this.request(`/employee-absences/delete/${id}`, {
      method: 'DELETE'
    });
  }

  async approveEmployeeAbsence(id) {
    return this.request(`/employee-absences/approve/${id}`, {
      method: 'PATCH'
    });
  }

  async rejectEmployeeAbsence(id, reason) {
    return this.request(`/employee-absences/reject/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ reason })
    });
  }

  async analyzeAbsenceImpact(data) {
    return this.request('/employee-absences/impact-analysis', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  // Teacher-specific: Get own absences
  async getMyAbsences(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/employee-absences/my-absences${queryString ? '?' + queryString : ''}`);
  }

  // Teacher-specific: Request absence (status forced to pending)
  async requestAbsence(data) {
    return this.request('/employee-absences/request', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  // Teacher-specific: Update own absence (can only update pending)
  async updateMyAbsence(id, data) {
    return this.request(`/employee-absences/update/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  async getAbsenceImpact(id) {
    return this.request(`/employee-absences/impact/${id}`);
  }

  // =====================
  // Attendance Management
  // =====================

  // Check-in a child
  async checkInChild(data) {
    return this.request('/attendance/check-in', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  // Check-out a child by attendance ID
  async checkOutChild(attendanceId, checkOutTime, notes = '') {
    return this.request('/attendance/check-out', {
      method: 'POST',
      body: JSON.stringify({ attendanceId, checkOutTime, notes })
    });
  }

  // Check-out a child by child ID (finds today's check-in)
  async checkOutChildById(childId, checkOutTime, notes = '') {
    return this.request('/attendance/check-out-by-child', {
      method: 'POST',
      body: JSON.stringify({ childId, checkOutTime, notes })
    });
  }

  // Get live attendance for a group (currently checked in children)
  async getLiveAttendanceForGroup(groupId, date = null) {
    const queryString = date ? `?date=${date}` : '';
    return this.request(`/attendance/group/${groupId}/live${queryString}`);
  }

  // Get live attendance for entire kindergarten (breakdown by group)
  async getLiveAttendanceForKindergarten(kindergartenId, date = null) {
    const queryString = date ? `?date=${date}` : '';
    return this.request(`/attendance/kindergarten/${kindergartenId}/live${queryString}`);
  }

  // Get all attendance records for a group on a specific date
  async getGroupAttendanceByDate(groupId, date) {
    return this.request(`/attendance/group/${groupId}/date/${date}`);
  }

  // Get all attendance records for a kindergarten on a specific date
  async getKindergartenAttendanceByDate(kindergartenId, date) {
    return this.request(`/attendance/kindergarten/${kindergartenId}/date/${date}`);
  }

  // Update an attendance record (e.g., change group, update notes)
  async updateAttendance(attendanceId, data) {
    return this.request(`/attendance/${attendanceId}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  // Delete an attendance record
  async deleteAttendance(attendanceId) {
    return this.request(`/attendance/${attendanceId}`, {
      method: 'DELETE'
    });
  }

  // Mark a child as absent for a specific date
  async markChildAbsent(childId, date = null, notes = '') {
    return this.request('/attendance/mark-absent', {
      method: 'POST',
      body: JSON.stringify({ childId, date, notes })
    });
  }

  // =====================
  // Staff Absence Management & Shift Swapping
  // =====================

  // Mark a duty assignment as absent
  async markAssignmentAbsent(assignmentId, absenceType, absenceReason = '') {
    return this.request(`/duty-assignments/mark-absent/${assignmentId}`, {
      method: 'POST',
      body: JSON.stringify({ absenceType, absenceReason })
    });
  }

  // Find replacement candidates for an absent shift
  async findReplacementCandidates(assignmentId, maxWeeklyHours = 40) {
    return this.request('/duty-assignments/find-replacements', {
      method: 'POST',
      body: JSON.stringify({ assignmentId, maxWeeklyHours })
    });
  }

  // Assign a replacement for an absent shift
  async assignReplacement(absentAssignmentId, replacementStaffId) {
    return this.request('/duty-assignments/assign-replacement', {
      method: 'POST',
      body: JSON.stringify({ absentAssignmentId, replacementStaffId })
    });
  }

  // ============================================================
  // TARIFF REGULATION API
  // ============================================================

  // Get all tariff regulations for a kindergarten
  async getTariffRegulations(kindergartenId) {
    return this.request(`/tariff-regulations?kindergartenId=${kindergartenId}`);
  }

  // Get active tariff regulation for a kindergarten
  async getActiveTariffRegulation(kindergartenId) {
    return this.request(`/tariff-regulations/active?kindergartenId=${kindergartenId}`);
  }

  // Get single tariff regulation with details
  async getTariffRegulation(id) {
    return this.request(`/tariff-regulations/${id}`);
  }

  // Create new tariff regulation
  async createTariffRegulation(data) {
    return this.request('/tariff-regulations/create', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  // Update tariff regulation (only if not active)
  async updateTariffRegulation(id, data) {
    return this.request(`/tariff-regulations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  // Activate a tariff regulation
  async activateTariffRegulation(id) {
    return this.request(`/tariff-regulations/${id}/activate`, {
      method: 'PATCH'
    });
  }

  // Deactivate a tariff regulation
  async deactivateTariffRegulation(id) {
    return this.request(`/tariff-regulations/${id}/deactivate`, {
      method: 'PATCH'
    });
  }

  // Delete tariff regulation (soft delete)
  async deleteTariffRegulation(id) {
    return this.request(`/tariff-regulations/${id}`, {
      method: 'DELETE'
    });
  }

  // ============================================================
  // SUBSIDIZED TARIFF TABLES API
  // ============================================================

  // Get all subsidized tables for a regulation (all age categories)
  async getSubsidizedTables(regulationId) {
    return this.request(`/tariff-regulations/${regulationId}/subsidized-tables`);
  }

  // Get subsidized table for a specific age category
  async getSubsidizedTableByCategory(regulationId, categoryId) {
    return this.request(`/tariff-regulations/${regulationId}/subsidized-tables/category/${categoryId}`);
  }

  // Bulk create/update subsidized table entries
  async bulkCreateSubsidizedEntries(regulationId, data) {
    return this.request(`/tariff-regulations/${regulationId}/subsidized-tables/bulk`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  // Import subsidized table from parsed Excel data
  async importSubsidizedTable(regulationId, data) {
    return this.request(`/tariff-regulations/${regulationId}/subsidized-tables/import`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  // Create a new subsidized table entry
  async createSubsidizedEntry(regulationId, data) {
    return this.request(`/tariff-regulations/${regulationId}/subsidized-tables`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  // Update a single subsidized table entry
  async updateSubsidizedEntry(regulationId, entryId, data) {
    return this.request(`/tariff-regulations/${regulationId}/subsidized-tables/${entryId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  // Delete a single subsidized table entry
  async deleteSubsidizedEntry(regulationId, entryId) {
    return this.request(`/tariff-regulations/${regulationId}/subsidized-tables/${entryId}`, {
      method: 'DELETE'
    });
  }

  // Clear all entries for a specific age category
  async clearSubsidizedTableCategory(regulationId, categoryId) {
    return this.request(`/tariff-regulations/${regulationId}/subsidized-tables/category/${categoryId}/clear`, {
      method: 'DELETE'
    });
  }

  // ============================================================
  // CHILD VOUCHER API
  // ============================================================

  // Get all child vouchers for admin (with optional filters)
  async getChildVouchers(filters = {}) {
    const params = new URLSearchParams(filters);
    return this.request(`/child-vouchers?${params.toString()}`);
  }

  // Get voucher data for a specific child
  async getChildVoucher(childId) {
    return this.request(`/child-vouchers/child/${childId}`);
  }

  // Get single voucher by ID
  async getVoucherById(voucherId) {
    return this.request(`/child-vouchers/${voucherId}`);
  }

  // Create or update child voucher
  async saveChildVoucher(voucherData) {
    return this.request('/child-vouchers', {
      method: 'POST',
      body: JSON.stringify(voucherData)
    });
  }

  // Update existing voucher
  async updateChildVoucher(voucherId, voucherData) {
    return this.request(`/child-vouchers/${voucherId}`, {
      method: 'PUT',
      body: JSON.stringify(voucherData)
    });
  }

  // Delete child voucher
  async deleteChildVoucher(voucherId) {
    return this.request(`/child-vouchers/${voucherId}`, {
      method: 'DELETE'
    });
  }

  // Calculate voucher eligibility for a child
  async calculateVoucherEligibility(voucherId) {
    return this.request(`/child-vouchers/${voucherId}/calculate-eligibility`, {
      method: 'POST'
    });
  }

  // Get vouchers expiring soon
  async getExpiringVouchers(days = 30) {
    return this.request(`/child-vouchers/expiring-soon/${days}`);
  }

  // ============================================================
  // VOUCHER VALIDATION API
  // ============================================================

  // Check if child has valid voucher for today or specific date
  async checkVoucherValidity(childId, date = null) {
    const params = date ? `?date=${date}` : '';
    return this.request(`/voucher-validation/child/${childId}/check${params}`);
  }

  // Check voucher validity for a period (month, week, etc.)
  async checkVoucherValidityForPeriod(childId, startDate, endDate) {
    return this.request(`/voucher-validation/child/${childId}/check-period`, {
      method: 'POST',
      body: JSON.stringify({ startDate, endDate })
    });
  }

  // Check if child has subsidized days but no valid voucher (mismatch)
  async checkVoucherMismatch(childId) {
    return this.request(`/voucher-validation/child/${childId}/mismatch-check`);
  }

  // Batch check voucher validity for multiple children
  async batchCheckVoucherValidity(childIds) {
    return this.request('/voucher-validation/batch-check', {
      method: 'POST',
      body: JSON.stringify({ childIds })
    });
  }

  // ============================================================
  // BOOKING TYPE API
  // ============================================================

  // Bulk update booking types for multiple children/days
  async bulkUpdateBookingTypes(updates) {
    return this.request('/booking-types/bulk-update', {
      method: 'POST',
      body: JSON.stringify({ updates })
    });
  }

  // Get booking type suggestion for a child based on voucher eligibility
  async suggestBookingType(childId) {
    return this.request('/booking-types/suggest', {
      method: 'POST',
      body: JSON.stringify({ childId })
    });
  }

  // Get booking type statistics
  async getBookingTypeStatistics(groupId = null) {
    const params = groupId ? `?groupId=${groupId}` : '';
    return this.request(`/booking-types/statistics${params}`);
  }

  // Auto-assign booking types based on voucher eligibility
  async autoAssignBookingTypes(groupId = null, dryRun = false) {
    return this.request('/booking-types/auto-assign', {
      method: 'POST',
      body: JSON.stringify({ groupId, dryRun })
    });
  }

  // ============================================================
  // BILLING CALCULATION API
  // ============================================================

  // Calculate billing for a single day
  async calculateDayBilling(params) {
    return this.request('/billing/calculate-day', {
      method: 'POST',
      body: JSON.stringify(params)
    });
  }

  // Calculate billing for a period
  async calculatePeriodBilling(params) {
    return this.request('/billing/calculate-period', {
      method: 'POST',
      body: JSON.stringify(params)
    });
  }

  // Preview monthly billing based on child's weekly schedule
  async previewMonthlyBilling(childId, year, month, hasSpecialNeeds = false) {
    return this.request('/billing/preview-month', {
      method: 'POST',
      body: JSON.stringify({ childId, year, month, hasSpecialNeeds })
    });
  }

  // Get billing summary for a child
  async getBillingSummary(childId, startDate = null, endDate = null, hasSpecialNeeds = false) {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (hasSpecialNeeds) params.append('hasSpecialNeeds', 'true');

    const queryString = params.toString();
    const url = `/billing/summary/${childId}${queryString ? '?' + queryString : ''}`;
    return this.request(url);
  }

  // Batch preview billing for multiple children
  async batchPreviewBilling(childIds, year, month) {
    return this.request('/billing/batch-preview', {
      method: 'POST',
      body: JSON.stringify({ childIds, year, month })
    });
  }

  // ============================================================
  // TARIFF INVOICE GENERATION API
  // ============================================================

  // Generate invoice from tariff billing for a single child
  async generateTariffInvoice(params) {
    return this.request('/tariff-invoices/generate', {
      method: 'POST',
      body: JSON.stringify(params)
    });
  }

  // Generate invoices for multiple children
  async generateBatchTariffInvoices(params) {
    return this.request('/tariff-invoices/generate-batch', {
      method: 'POST',
      body: JSON.stringify(params)
    });
  }

  // Generate invoices for all children in a group
  async generateGroupTariffInvoices(params) {
    return this.request('/tariff-invoices/generate-group', {
      method: 'POST',
      body: JSON.stringify(params)
    });
  }

  // Generate invoices for all children in kindergarten
  async generateAllTariffInvoices(params) {
    return this.request('/tariff-invoices/generate-all', {
      method: 'POST',
      body: JSON.stringify(params)
    });
  }

  // ============================================================
  // TARIFF REPORTS & STATISTICS API
  // ============================================================

  // Get overall tariff system statistics
  async getTariffOverview() {
    return this.request('/tariff-reports/overview');
  }

  // Get voucher usage statistics
  async getVoucherUsageStats() {
    return this.request('/tariff-reports/voucher-usage');
  }

  // Get revenue summary by booking type
  async getRevenueSummary(startDate = null, endDate = null, kindergartenId = null) {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (kindergartenId) params.append('kindergartenId', kindergartenId);

    const queryString = params.toString();
    const url = `/tariff-reports/revenue-summary${queryString ? '?' + queryString : ''}`;
    return this.request(url);
  }

  // Get monthly billing statistics
  async getMonthlyBillingStats(year, month) {
    return this.request(`/tariff-reports/monthly-billing?year=${year}&month=${month}`);
  }

  // ========== TEACHER PERMISSION SYSTEM API ==========

  // Get my permissions (for teachers/directors)
  async getMyPermissions() {
    return this.request('/teacher-permissions/my-permissions');
  }

  // Get all permissions for admin
  async getTeacherPermissions(filters = {}) {
    const params = new URLSearchParams();
    if (filters.kindergartenId) params.append('kindergartenId', filters.kindergartenId);
    if (filters.isActive !== undefined) params.append('isActive', filters.isActive);

    const queryString = params.toString();
    const url = `/teacher-permissions${queryString ? '?' + queryString : ''}`;
    return this.request(url);
  }

  // Get permissions for a specific kindergarten
  async getKindergartenPermissions(kindergartenId) {
    return this.request(`/teacher-permissions/kindergarten/${kindergartenId}`);
  }

  // Get permissions for a specific teacher
  async getTeacherPermission(teacherId) {
    return this.request(`/teacher-permissions/teacher/${teacherId}`);
  }

  // Grant director permissions to a teacher
  async grantTeacherPermissions(teacherId, kindergartenId, grantedModules) {
    return this.request('/teacher-permissions/grant', {
      method: 'POST',
      body: JSON.stringify({
        teacherId,
        kindergartenId,
        grantedModules
      }),
    });
  }

  // Update teacher permissions
  async updateTeacherPermissions(permissionId, grantedModules) {
    return this.request(`/teacher-permissions/update/${permissionId}`, {
      method: 'PATCH',
      body: JSON.stringify({ grantedModules }),
    });
  }

  // Revoke teacher permissions
  async revokeTeacherPermissions(permissionId) {
    return this.request(`/teacher-permissions/revoke/${permissionId}`, {
      method: 'PATCH',
    });
  }

  // Delete permission record permanently
  async deleteTeacherPermission(permissionId) {
    return this.request(`/teacher-permissions/${permissionId}`, {
      method: 'DELETE',
    });
  }

  // Get audit logs
  async getPermissionAuditLogs(filters = {}) {
    const params = new URLSearchParams();
    if (filters.teacherId) params.append('teacherId', filters.teacherId);
    if (filters.kindergartenId) params.append('kindergartenId', filters.kindergartenId);
    if (filters.performedBy) params.append('performedBy', filters.performedBy);
    if (filters.action) params.append('action', filters.action);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.skip) params.append('skip', filters.skip);

    const queryString = params.toString();
    const url = `/permission-audit-logs${queryString ? '?' + queryString : ''}`;
    return this.request(url);
  }

  // Get audit logs for a specific teacher
  async getTeacherAuditLogs(teacherId, limit = 20) {
    return this.request(`/permission-audit-logs/teacher/${teacherId}?limit=${limit}`);
  }

  // Get audit logs for a specific kindergarten
  async getKindergartenAuditLogs(kindergartenId, limit = 50) {
    return this.request(`/permission-audit-logs/kindergarten/${kindergartenId}?limit=${limit}`);
  }

  // Get audit log statistics
  async getPermissionAuditStats(filters = {}) {
    const params = new URLSearchParams();
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);

    const queryString = params.toString();
    const url = `/permission-audit-logs/stats${queryString ? '?' + queryString : ''}`;
    return this.request(url);
  }

  // ============================================================
  // Backup & Restore API
  // ============================================================

  // Get all backups for current admin
  async getBackups() {
    return this.request('/backups');
  }

  // Create new backup
  async createBackup(description = '') {
    return this.request('/backups/create', {
      method: 'POST',
      body: JSON.stringify({ description }),
    });
  }

  // Get specific backup details
  async getBackup(backupId) {
    return this.request(`/backups/${backupId}`);
  }

  // Restore backup
  async restoreBackup(backupId) {
    return this.request(`/backups/restore/${backupId}`, {
      method: 'POST',
    });
  }

  // Delete backup
  async deleteBackup(backupId) {
    return this.request(`/backups/${backupId}`, {
      method: 'DELETE',
    });
  }

  // Delete all data for admin
  async deleteAllData() {
    return this.request('/backups/delete-all-data', {
      method: 'POST',
    });
  }

  // Restore from uploaded backup file
  async uploadAndRestoreBackup({ backupData, sourceAdminId }) {
    return this.request('/backups/restore-from-upload', {
      method: 'POST',
      body: JSON.stringify({ backupData, sourceAdminId }),
    });
  }

  // ===== Maintenance Mode API =====
  async getMaintenanceWindows() {
    return this.request('/maintenance');
  }

  async getActiveMaintenance() {
    return this.request('/maintenance/active');
  }

  async createMaintenanceWindow(data) {
    return this.request('/maintenance/create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateMaintenanceWindow(id, data) {
    return this.request(`/maintenance/update/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteMaintenanceWindow(id) {
    return this.request(`/maintenance/delete/${id}`, {
      method: 'DELETE',
    });
  }

  // ===== Email Settings API =====
  async getEmailSettings() {
    return this.request('/email-settings');
  }

  async saveEmailSettings(data) {
    return this.request('/email-settings/save', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async testEmailSettings() {
    return this.request('/email-settings/test', {
      method: 'POST',
    });
  }

  async toggleEmailSettings() {
    return this.request('/email-settings/toggle', {
      method: 'PATCH',
    });
  }

  async deleteEmailSettings() {
    return this.request('/email-settings/delete', {
      method: 'DELETE',
    });
  }

  // ===== App Version API =====
  async getAppVersions() {
    return this.request('/app-version');
  }

  async checkAppVersion(platform, currentVersion) {
    return this.request(`/app-version/check?platform=${encodeURIComponent(platform)}&currentVersion=${encodeURIComponent(currentVersion)}`);
  }

  async createAppVersion(data) {
    return this.request('/app-version/create', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAppVersion(id, data) {
    return this.request(`/app-version/update/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteAppVersion(id) {
    return this.request(`/app-version/delete/${id}`, {
      method: 'DELETE',
    });
  }

  // ===== Feature Flags API =====
  // Read flags for the current tenant (teachers, parents, admin all use this)
  async getFeatureFlags() {
    return this.request('/feature-flags/public');
  }

  // Admin reads their own flags for the FeatureFlagsManager UI
  async getAdminFeatureFlags() {
    return this.request('/feature-flags');
  }

  // Admin saves updated flags  { features: { messagingComposeButton: { parents: true, teachers: false } } }
  async updateFeatureFlags(data) {
    return this.request('/feature-flags', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // Thread creator locks or unlocks replies on a specific thread
  async toggleThreadReplies(threadId, repliesDisabled) {
    return this.request(`/threads/${threadId}/toggle-replies`, {
      method: 'PATCH',
      body: JSON.stringify({ repliesDisabled }),
    });
  }

  // Mute or unmute a thread for the current user (suppresses push + email)
  async toggleThreadMute(threadId, muted) {
    return this.request(`/threads/${threadId}/toggle-mute`, {
      method: 'PATCH',
      body: JSON.stringify({ muted }),
    });
  }

  // Edit a thread's coowner list and parent recipient list atomically.
  // Server authorizes creator OR any current coowner; rejects self-removal by a coowner.
  async updateThreadRoster(threadId, { coOwnerIds, recipientIds }) {
    return this.request(`/threads/${threadId}/roster`, {
      method: 'PATCH',
      body: JSON.stringify({ coOwnerIds, recipientIds }),
    });
  }

  // Edit an approval request's co-author list and parent recipient list atomically.
  // Server authorizes sender OR any current co-author; rejects self-removal by a co-author.
  // Removed parents are preserved (red-marked) and stop seeing/counting the request.
  async updateApprovalRoster(messageId, { coOwnerIds, recipientIds }) {
    return this.request(`/messages/${messageId}/approval-roster`, {
      method: 'PATCH',
      body: JSON.stringify({ coOwnerIds, recipientIds }),
    });
  }

  // ===== Quizzes =====
  // A quiz on a new message travels inside sendInfoMessage / sendApprovalMessage
  // ({ quiz, batchId }); createQuiz adds one to an existing approval request.
  async createQuiz(messageId, quiz) {
    return this.request('/quizzes', {
      method: 'POST',
      body: JSON.stringify({ messageId, quiz }),
    });
  }

  async getQuiz(quizId) {
    return this.request(`/quizzes/${quizId}`);
  }

  // { questions?, settings?, closesAt?, parentOptionsOff? }
  async updateQuiz(quizId, changes) {
    return this.request(`/quizzes/${quizId}`, {
      method: 'PATCH',
      body: JSON.stringify(changes),
    });
  }

  // The parent's whole answer set; [] takes the answer back. confirm also
  // confirms the approval request it belongs to.
  async answerQuiz(quizId, answers, { confirm = false } = {}) {
    return this.request(`/quizzes/${quizId}/answers`, {
      method: 'POST',
      body: JSON.stringify({ answers, ...(confirm ? { confirm: true } : {}) }),
    });
  }

  async addQuizOption(quizId, questionId, label) {
    return this.request(`/quizzes/${quizId}/options`, {
      method: 'POST',
      body: JSON.stringify({ questionId, label }),
    });
  }

  async removeQuizOption(quizId, optionId) {
    return this.request(`/quizzes/${quizId}/options/${optionId}`, {
      method: 'DELETE',
    });
  }

  async closeQuiz(quizId) {
    return this.request(`/quizzes/${quizId}/close`, { method: 'POST', body: JSON.stringify({}) });
  }

  async reopenQuiz(quizId, closesAt) {
    return this.request(`/quizzes/${quizId}/reopen`, {
      method: 'POST',
      body: JSON.stringify(closesAt === undefined ? {} : { closesAt }),
    });
  }

  async remindQuiz(quizId) {
    return this.request(`/quizzes/${quizId}/remind`, { method: 'POST', body: JSON.stringify({}) });
  }

  async getQuizAnswers(quizId) {
    return this.request(`/quizzes/${quizId}/answers`);
  }

  async getQuizExport(quizId) {
    return this.request(`/quizzes/${quizId}/export`);
  }

  // One Compose send to several groups: this quiz with the others of the
  // same send that the caller manages, per group and combined.
  async getQuizBatch(quizId) {
    return this.request(`/quizzes/${quizId}/batch`);
  }

  async getQuizBatchExport(quizId) {
    return this.request(`/quizzes/${quizId}/batch/export`);
  }

  // "Parents can see each other" on a conversation. Creator, co-owners (staff) and admin.
  async setThreadParentsVisibility(threadId, parentsSeeEachOther) {
    return this.request(`/threads/${threadId}/parents-visibility`, {
      method: 'PATCH',
      body: JSON.stringify({ parentsSeeEachOther }),
    });
  }

  // "Parents can see each other" on an approval request. Sender, co-authors and admin.
  async setApprovalParentsVisibility(messageId, parentsSeeEachOther) {
    return this.request(`/messages/${messageId}/parents-visibility`, {
      method: 'PATCH',
      body: JSON.stringify({ parentsSeeEachOther }),
    });
  }

  // Download backup as JSON file
  async downloadBackup(backupId) {
    const appId = getAppIdentifier();
    const response = await fetch(`${API_BASE}/backups/${backupId}`, {
      credentials: 'include',
      headers: {
        'X-App-Identifier': appId || '',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to download backup: ${response.statusText}`);
    }

    return response.blob();
  }

  // ============================================================
  // Data Manager API
  // ============================================================

  async getDataOverview() {
    return this.request('/data-manager/overview');
  }

  async previewDataCleanup(category, cutoffDate) {
    return this.request('/data-manager/preview-cleanup', {
      method: 'POST',
      body: JSON.stringify({ category, cutoffDate }),
    });
  }

  async executeDataCleanup(category, cutoffDate) {
    return this.request('/data-manager/cleanup', {
      method: 'POST',
      body: JSON.stringify({ category, cutoffDate }),
    });
  }

  // Flag-based messaging archive (messages category archives in place,
  // shared with the teacher-facing Messaging Hub archive)
  async getMessagingArchiveSummary() {
    return this.request('/data-manager/messaging-archive/summary');
  }

  async previewMessagingArchive(cutoffDate) {
    return this.request(`/data-manager/messaging-archive/preview?cutoffDate=${encodeURIComponent(cutoffDate)}`);
  }

  async restoreAllMessagingArchive(before) {
    return this.request('/data-manager/messaging-archive/restore-all', {
      method: 'POST',
      body: JSON.stringify(before ? { before } : {}),
    });
  }

  // Flag-based absence-request archive (admin bulk; teachers retrieve via
  // the archived-absences modal)
  async getAbsenceArchiveSummary() {
    return this.request('/data-manager/absence-archive/summary');
  }

  async previewAbsenceArchive(cutoffDate) {
    return this.request(`/data-manager/absence-archive/preview?cutoffDate=${encodeURIComponent(cutoffDate)}`);
  }

  async restoreAllAbsenceArchive(before) {
    return this.request('/data-manager/absence-archive/restore-all', {
      method: 'POST',
      body: JSON.stringify(before ? { before } : {}),
    });
  }

  async executeDataArchive(category, cutoffDate) {
    return this.request('/data-manager/archive', {
      method: 'POST',
      body: JSON.stringify({ category, cutoffDate }),
    });
  }

  async getArchiveRuns() {
    return this.request('/data-manager/archives');
  }

  async getArchiveItems(runId, { search, model, page, limit } = {}) {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (model) params.append('model', model);
    if (page) params.append('page', page);
    if (limit) params.append('limit', limit);
    const query = params.toString();
    return this.request(`/data-manager/archives/${runId}/items${query ? `?${query}` : ''}`);
  }

  async restoreArchiveItems(runId, payload = {}) {
    return this.request('/data-manager/archives/restore', {
      method: 'POST',
      body: JSON.stringify({ runId, ...payload }),
    });
  }

  async purgeArchiveRun(runId) {
    return this.request(`/data-manager/archives/${runId}`, {
      method: 'DELETE',
    });
  }

  // Save user language preference to backend (for localized push notifications)
  async saveLanguage(language) {
    return this.request('/users/language', {
      method: 'PUT',
      body: JSON.stringify({ language }),
    });
  }
}

export default new ApiService();